"use client";

import { useRouter } from "next/router";
import { useCallback } from "react";

import { PageHero } from "@widgets/page-hero";

import { RulesSection } from "@features/rules-section/ui/rules-section";
import { TicketCard } from "@features/ticket-card/ui/ticket-card";

import { DocumentsSection } from "@entities/document";

import { FilterPaginationContainer } from "@shared/ui/filter-pagination-container/filter-pagination-container";

import { getCurrentTickets } from "../api/getCurrentTickets";
import type { TicketsPageType, TicketsType, TicketTypesType } from "../model";

import s from "./tickets-page.module.scss";

export type TicketsPageProps = {
  filters: TicketTypesType[];
  tickets: TicketsType;
  pageData: TicketsPageType;
};

export const TicketsPage = (props: TicketsPageProps) => {
  const { filters, tickets, pageData } = props;

  const route = useRouter();

  const handleGetCurrentTickets = useCallback(
    (filter: string, count: number) => {
      const result = getCurrentTickets(filter, count);

      const currentType =
        typeof route.query.type === "string" ? route.query.type : undefined;

      if (filter === "all") {
        if (currentType !== undefined) {
          void route.push({ pathname: "/tickets" }, undefined, {
            shallow: true,
          });
        }
        return result;
      }

      if (currentType !== filter) {
        void route.push(
          {
            pathname: "/tickets",
            query: { type: filter },
          },
          undefined,
          { shallow: true },
        );
      }

      return result;
    },
    [route],
  );

  return (
    <main className={s.root}>
      {pageData?.titleWithIcon && (
        <PageHero
          variant="primary"
          textColor="brown"
          title={pageData.titleWithIcon}
        />
      )}
      <FilterPaginationContainer
        initialContent={tickets}
        filters={filters}
        fetchFunction={handleGetCurrentTickets}
        renderCard={(ticket, index) => (
          <TicketCard
            key={`ticket_card-${ticket.title}-${index + 1}`}
            data={ticket}
          />
        )}
        showFilters={true}
        paginationThreshold={6}
        cardsClassName={s.cards}
      />
      {pageData?.rulesSection && pageData.rulesSection.rules?.length > 0 && (
        <RulesSection data={pageData.rulesSection} />
      )}
      {pageData?.documentSection &&
        pageData.documentSection.documents?.length > 0 && (
          <DocumentsSection data={pageData.documentSection} />
        )}
    </main>
  );
};

TicketsPage.displayName = "TicketsPage";
